<template>
  <div>
    <div class="text-l-medium text-black-white mb-1 mt-4">RGB</div>
    <div class="flex gap-2">
      <Button
        color="normal"
        size="sm"
        shape="corner"
        class="w-22 py-1.5 px-3 active:bg-key active:text-black-white"
        :class="{ 'bg-neutral-dark-disabled active:bg-neutral-dark-disabled': disabled }"
      >
        <Input
          :key="disabled ? 'r-off' : 'r-on'"
          :model-value="displayR"
          size="sm"
          type="text"
          inputmode="numeric"
          maxlength="3"
          :disabled="disabled"
          class="w-full p-0 text-center text-black-white hover:text-text-glass-hover text-s-medium bg-transparent disabled:bg-transparent disabled:text-black-white hover:ring-0 focus-visible:ring-0"
          @update:model-value="(val) => handleDraft('r', val)"
          @input="handleInput"
          @blur="commitChannel('r')"
          @keydown.enter.prevent="handleEnter"
        />
      </Button>
      <Button
        color="normal"
        size="sm"
        shape="corner"
        class="w-22 py-1.5 px-3 active:bg-key active:text-black-white"
        :class="{ 'bg-neutral-dark-disabled active:bg-neutral-dark-disabled': disabled }"
      >
        <Input
          :key="disabled ? 'g-off' : 'g-on'"
          :model-value="displayG"
          size="sm"
          type="text"
          inputmode="numeric"
          maxlength="3"
          :disabled="disabled"
          class="w-full p-0 text-center text-black-white hover:text-text-glass-hover text-s-medium bg-transparent disabled:bg-transparent disabled:text-black-white hover:ring-0 focus-visible:ring-0"
          @update:model-value="(val) => handleDraft('g', val)"
          @input="handleInput"
          @blur="commitChannel('g')"
          @keydown.enter.prevent="handleEnter"
        />
      </Button>
      <Button
        color="normal"
        size="sm"
        shape="corner"
        class="w-22 py-1.5 px-3 active:bg-key active:text-black-white"
        :class="{ 'bg-neutral-dark-disabled active:bg-neutral-dark-disabled': disabled }"
      >
        <Input
          :key="disabled ? 'b-off' : 'b-on'"
          :model-value="displayB"
          size="sm"
          type="text"
          inputmode="numeric"
          maxlength="3"
          :disabled="disabled"
          class="w-full p-0 text-center text-black-white hover:text-text-glass-hover text-s-medium bg-transparent disabled:bg-transparent disabled:text-black-white hover:ring-0 focus-visible:ring-0"
          @update:model-value="(val) => handleDraft('b', val)"
          @input="handleInput"
          @blur="commitChannel('b')"
          @keydown.enter.prevent="handleEnter"
        />
      </Button>
    </div>
  </div>
</template>
<script setup lang="ts">
  import type { RGB } from '@rapoo/device-sdk'

  import { watch, ref, computed } from 'vue'

  import { Button, Input } from '@rapoo/element'
  import { RGB_CHANNEL_MAX, sanitizeRgbChannel, syncInputElement } from './colorInputRules'

  type Channel = 'r' | 'g' | 'b'

  const props = withDefaults(
    defineProps<{
      modelValue?: RGB
      /** 无法修改颜色时禁用输入并显示 / */
      disabled?: boolean
    }>(),
    {
      modelValue: () => ({ r: 0, g: 0, b: 0 }),
      disabled: false,
    },
  )
  const emit = defineEmits(['update:modelValue', 'change'])

  /** 输入草稿：编辑中不立刻同步到父级 */
  const draft = ref({
    r: String(props.modelValue.r),
    g: String(props.modelValue.g),
    b: String(props.modelValue.b),
  })
  const editingChannel = ref<Channel | null>(null)

  const displayR = computed(() => (props.disabled ? '/' : draft.value.r))
  const displayG = computed(() => (props.disabled ? '/' : draft.value.g))
  const displayB = computed(() => (props.disabled ? '/' : draft.value.b))

  watch(
    () => props.modelValue,
    (val) => {
      if (editingChannel.value !== 'r') draft.value.r = String(val.r)
      if (editingChannel.value !== 'g') draft.value.g = String(val.g)
      if (editingChannel.value !== 'b') draft.value.b = String(val.b)
    },
  )

  const dataFormat = (val: number | string) => {
    if (val === '' || val === undefined || val === null || Number.isNaN(+val)) return 0
    return Math.min(Math.max(0, +val), RGB_CHANNEL_MAX)
  }

  const handleDraft = (channel: Channel, val: string | number) => {
    if (props.disabled) return
    editingChannel.value = channel
    draft.value[channel] = sanitizeRgbChannel(String(val))
  }

  const handleInput = (event: Event) => {
    if (props.disabled) return
    syncInputElement(event, sanitizeRgbChannel)
  }

  const commitChannel = (channel: Channel) => {
    if (props.disabled) return
    editingChannel.value = null
    const next = dataFormat(draft.value[channel])
    draft.value[channel] = String(next)
    if (next === props.modelValue[channel]) return
    const newRGB = { ...props.modelValue, [channel]: next }
    emit('update:modelValue', newRGB)
    emit('change', newRGB)
  }

  const handleEnter = (e: KeyboardEvent) => {
    ;(e.target as HTMLInputElement).blur()
  }
</script>
